import {
  AgentError,
  type AgentErrorType,
  type AgentInput,
  type AgentOutput,
  type AgentStreamChunk,
  type AgentStreamMessage,
  type Data,
  type Dimensions,
} from "schema/agent";
import type { AgentFiles, Message } from "./types";

const STREAM_DELIMITER = "\n\n";

export interface ChatContext {
  history: Message[];
  files: AgentFiles;
  dimensions: Dimensions;
  data: Data;
  userCredits: number;
}

export interface ChatStreamHandlers {
  onChunk: (update: AgentStreamMessage) => void;
  onError: (type: AgentErrorType, msg: string) => void;
}

export async function streamFlow(
  input: AgentInput,
  onChunk: (chunk: AgentStreamMessage) => void,
): Promise<AgentOutput> {
  const response = await fetch("/api/agent", {
    method: "POST",
    body: JSON.stringify({ data: input }),
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
  });

  if (response.status !== 200) {
    if (response.status === 400) {
      const json: { message: string } = await response.json();
      throw new AgentError("INVALID_ARGUMENT", json.message);
    } else if (response.status === 401) {
      throw new AgentError("UNAUTHENTICATED", "User is not authenticated. Please log in again.");
    } else {
      const text = await response.text();
      throw new AgentError("INTERNAL", `HTTP error ${response.status}, response text: ${text}`);
    }
  }

  if (!response.body) {
    throw new AgentError("INTERNAL", "Response body is empty");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = "";
  while (true) {
    const result = await reader.read();
    const decodedValue = decoder.decode(result.value);
    if (decodedValue) {
      buffer += decodedValue;
    }

    while (buffer.includes(STREAM_DELIMITER)) {
      const rawChunk = buffer
        .substring(0, buffer.indexOf(STREAM_DELIMITER))
        .substring("data: ".length);

      let chunk: AgentStreamChunk = {};
      try {
        chunk = JSON.parse(rawChunk);
      } catch {
        throw new AgentError("INTERNAL", `Fail to parse chunk to JSON, rawChunk: ${rawChunk}`);
      }

      if (chunk.message) {
        onChunk(chunk.message);
      } else if (chunk.result) {
        return chunk.result;
      } else if (chunk.error) {
        const error = chunk.error;
        throw new AgentError(error.type, error.message);
      } else {
        throw new AgentError("INTERNAL", `Invalid chunk format: ${rawChunk}`);
      }
      buffer = buffer.substring(buffer.indexOf(STREAM_DELIMITER) + STREAM_DELIMITER.length);
    }
  }
}

export const streamChat = async (
  projectId: string,
  message: string,
  { history, files, dimensions, data, userCredits }: ChatContext,
  { onChunk, onError }: ChatStreamHandlers,
): Promise<number | undefined> => {
  const input = {
    message,
    projectId,
    userCredits,
    checkpoint: history.at(-1)?.checkpoint,
    history: history.map((msg) => ({
      role: msg.role,
      content: msg.content,
    })),
    context: {
      init: files.init,
      render: files.render,
      helpers: files.helpers,
      assets: files.assets?.map((asset) => ({
        id: asset.id,
        name: asset.name,
        mimeType: asset.mimeType,
      })),
      dimensions: {
        width: dimensions.width,
        height: dimensions.height,
      },
      data: data ?? undefined,
    },
  };

  try {
    const output = await streamFlow(input, onChunk);
    return output.totalCost;
  } catch (e) {
    if (e instanceof AgentError && e.type !== "INTERNAL") {
      onError(e.type, e.message);
    } else {
      if (!(e instanceof AgentError)) console.error(e);
      onError(
        "INTERNAL",
        "An unknown error occurred, please try again later. Contact support if the issue persists.",
      );
    }
  }
};
